import React, { useState, useEffect, useCallback } from 'react';
import baseapi from '../../../services/baseapi';
import { Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';

interface AlertsTabProps {
  agentId: string;
  clientId: string;
}

interface AlertData {
  category: string;
  severity: string;
  message: string;
  resource: string;
  namespace: string;
  cluster_name: string;
  timestamp: string;
}

const AlertsTab: React.FC<AlertsTabProps> = ({ agentId, clientId }) => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, severityFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;

      const queryObj: any = {};

      if (debouncedSearch && severityFilter) {
        queryObj.$and = [
          {
            $or: [
              { message: { "$ilike": `%${debouncedSearch}%` } },
              { resource: { "$ilike": `%${debouncedSearch}%` } },
              { category: { "$ilike": `%${debouncedSearch}%` } },
              { namespace: { "$ilike": `%${debouncedSearch}%` } }
            ]
          },
          { severity: severityFilter }
        ];
      } else if (debouncedSearch) {
        queryObj.$or = [
          { message: { "$ilike": `%${debouncedSearch}%` } },
          { resource: { "$ilike": `%${debouncedSearch}%` } },
          { category: { "$ilike": `%${debouncedSearch}%` } },
          { namespace: { "$ilike": `%${debouncedSearch}%` } }
        ];
      } else if (severityFilter) {
        queryObj.severity = severityFilter;
      }

      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = {
        agent_id: agentId,
        client_id: clientId,
        skip,
        limit: itemsPerPage
      };

      if (clusterName) {
        params.cluster_name = clusterName;
      }

      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }

      const res = await baseapi.get('/k8s_dashboard/alerts', { params });
      if (res.data?.status) {
        setAlerts(res.data.data || []);
        setTotalItems(res.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching alerts data:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, clientId, currentPage, itemsPerPage, debouncedSearch, severityFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + alerts.length;

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value: number): void => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const getSeverityStyle = (severity: string) => {
    const norm = (severity || '').toLowerCase();
    if (norm === 'critical' || norm === 'error') {
      return 'border-red-500/30 bg-red-500/10 text-red-400';
    }
    if (norm === 'warning' || norm === 'warn') {
      return 'border-amber-500/30 bg-amber-500/10 text-amber-400';
    }
    if (norm === 'info') {
      return 'border-blue-500/30 bg-blue-500/10 text-blue-400';
    }
    return 'border-gray-500/30 bg-gray-500/10 text-gray-400';
  };

  const getSeverityIcon = (severity: string) => {
    const norm = (severity || '').toLowerCase();
    if (norm === 'critical' || norm === 'error') {
      return <AlertCircle className="w-3.5 h-3.5 text-red-400 animate-pulse" />;
    }
    if (norm === 'warning' || norm === 'warn') {
      return <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
    }
    return <Info className="w-3.5 h-3.5 text-blue-400" />;
  };

  return (
    <div className="space-y-4 font-sans">
      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="text-gray-200 font-mono text-[11px] bg-gray-900/50 px-3 py-1.5 rounded-lg border border-gray-800">
          {totalItems > 0 ? `${startIndex + 1} - ${endIndex} of ${totalItems}` : '0 alerts'}
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center">
          <button
            onClick={handleRefresh}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-800/80 border border-gray-700 text-gray-200 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.2)]"
            title="Refresh alerts"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-200" />
            <input
              type="text"
              placeholder="search alert / resource / msg…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-gray-900/50 border border-gray-700/50 text-gray-200 text-sm rounded-lg pl-9 pr-4 py-2 w-full sm:w-64 focus:outline-none focus:border-orange-500/50 transition-colors placeholder:text-gray-600 font-mono h-10"
            />
          </div>
          <CustomDropdown
            data={[
              { label: 'all severities', value: '' },
              { label: 'Critical', value: 'critical' },
              { label: 'Warning', value: 'warning' },
              { label: 'Info', value: 'info' }
            ]}
            width="w-44"
            placeholder="all severities"
            selectedValue={severityFilter}
            onSelect={setStatusFilter => setSeverityFilter(setStatusFilter)}
            className="shadow-xl shadow-black/20 font-mono"
            buttonHeight={10}
          />
        </div>
      </div>

      <div className="bg-gray-800/90 backdrop-blur-md border border-white/5 rounded-xl mb-3 overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.02)]">
        <div className="overflow-x-auto custom-scroll max-h-[72vh]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-gray-800/80 sticky top-0 z-10 backdrop-blur-sm">
              <tr className="border-b border-gray-700/50">
                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest text-center">Severity</th>
                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest">Category</th>
                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest">Resource</th>
                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest">Namespace</th>
                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest">Message</th>
                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest">Cluster</th>
                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest">Time</th>
              </tr>
            </thead>
            <tbody>
              {alerts.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="py-8 px-6 text-center text-gray-200 font-mono text-[13px] bg-black/10 border-b border-white/5">
                    no active alerts match
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                      <p className="text-gray-200 font-medium">Loading alerts...</p>
                    </div>
                  </td>
                </tr>
              ) : (
                alerts.map((alert: AlertData, idx: number) => (
                  <tr
                    key={idx}
                    className="bg-gray-800 border-b border-gray-700/50 hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-3 py-2.5 text-center font-mono">
                      <span className={`inline-flex items-center gap-1.5 text-[9px] px-2 py-0.5 rounded border font-bold uppercase ${getSeverityStyle(alert.severity)}`}>
                        {getSeverityIcon(alert.severity)}
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-white font-bold whitespace-nowrap">{alert.category}</td>
                    <td className="px-3 py-2.5 text-gray-200 font-mono text-[11px] font-semibold">{alert.resource}</td>
                    <td className="px-3 py-2.5 text-gray-200 font-mono text-[11px] font-semibold">{alert.namespace || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-100 font-mono max-w-md break-all leading-normal select-text">{alert.message}</td>
                    <td className="px-3 py-2.5 text-gray-200 font-mono text-[11px] whitespace-nowrap">{alert.cluster_name || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-300 font-mono text-[11px] whitespace-nowrap">
                      {alert.timestamp ? new Date(alert.timestamp).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Table Footer / Pagination */}
          <div className="bg-gray-800/80 border-t border-gray-700 px-3 py-2 flex items-center justify-between sticky bottom-0 z-10 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <ItemsPerPageDropdown
                value={itemsPerPage}
                onChange={(val: any) => handleItemsPerPageChange(val)}
              />
              {totalItems > 0 ? (
                <span className="text-[10px] text-gray-200 font-bold uppercase tracking-widest font-mono">
                  <span>{startIndex + 1}</span> - <span>{endIndex}</span> OF {totalItems}
                </span>
              ) : (
                alerts.length > 0 && (
                  <span className="text-[10px] text-gray-200 font-bold uppercase tracking-widest font-mono">
                    <span>{startIndex + 1}</span> - <span>{startIndex + alerts.length}</span>
                  </span>
                )
              )}
            </div>
            <div className="flex items-center gap-1 font-mono">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-1 text-gray-200 hover:text-white disabled:opacity-30 transition-colors"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 text-gray-200 hover:text-white disabled:opacity-30 transition-colors"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="px-2 py-0.5 bg-orange-600 rounded text-white text-[10px] font-bold mx-1 shadow-md shadow-orange-950/20">
                {currentPage}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={alerts.length < itemsPerPage || (totalItems > 0 && currentPage === totalPages)}
                className="p-1 text-gray-200 hover:text-white disabled:opacity-30 transition-colors"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {totalItems > 0 && (
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-1 text-gray-200 hover:text-white disabled:opacity-30 transition-colors"
                  title="Last Page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertsTab;
